import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import Input from 'components/Input';
import RadioButton from 'components/RadioButton';
import Tooltip from 'components/Tooltip';
import Typography from 'components/Typography';

import { useSimulatorStore } from 'store/useSimulatorStore.ts';

import { PREVENTA, TERM } from './Quote.constants.ts';
import {
  QuoteButton,
  QuoteContainer,
  QuoteContent,
  QuoteTitle,
} from './Quote.styled.ts';

type FormData = {
  valor_inmueble: string;
  enganche: string;
  presupuesto_mensual: string;
  plazo: string;
  pago_preventa: string;
  preventa: string;
};

const Quote = () => {
  const navigate = useNavigate();
  const { form, saveQuote } = useSimulatorStore();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: form || {
      valor_inmueble: '',
      enganche: '',
      presupuesto_mensual: '',
      pago_preventa: '',
      plazo: '',
      preventa: '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: FormData) => {
    saveQuote(data);
    navigate('/datos-cliente/telefono');
  };

  return (
    <QuoteContainer>
      <QuoteContent>
        <QuoteTitle>
          <Typography type="Title" size="small" style="semibold">
            Cotiza el préstamo
          </Typography>
          <Typography type="Body" size="small" style="normal" color="#5C5C5C">
            Elige el plazo y el monto que se adapte a tu cliente.
          </Typography>
        </QuoteTitle>

        <Controller
          name="valor_inmueble"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => {
            return (
              <Input
                title="Valor del inmueble"
                placeholder="Valor en pesos mexicanos"
                maxLength={40}
                inputType="money"
                inputMode="numeric"
                value={value}
                onChange={onChange}
                isValid={!errors.valor_inmueble?.message && value.length > 0}
              />
            );
          }}
        />

        <Controller
          name="enganche"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => {
            return (
              <Input
                title="Enganche"
                placeholder="Valor en pesos mexicanos"
                maxLength={40}
                inputType="money"
                inputMode="numeric"
                helperText="Debe ser mínimo del 15% del valor del inmueble."
                value={value}
                onChange={onChange}
                isValid={!errors.enganche?.message && value.length > 0}
              />
            );
          }}
        />

        <Controller
          name="preventa"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => {
            return (
              <RadioButton
                title="¿Es preventa?"
                value={value}
                onChange={onChange}
                options={PREVENTA}
              />
            );
          }}
        />

        {watch('preventa') === 'no' && (
          <Controller
            key="presupuesto_mensual"
            name="presupuesto_mensual"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => {
              return (
                <Input
                  title="Ingresa el presupuesto mensual del cliente"
                  placeholder="Valor en pesos mexicanos"
                  maxLength={40}
                  inputMode="numeric"
                  inputType="money"
                  value={value}
                  onChange={onChange}
                  isValid={
                    !errors.presupuesto_mensual?.message && value.length > 0
                  }
                />
              );
            }}
          />
        )}

        {watch('preventa') === 'si' && (
          <Controller
            key="pago_preventa"
            name="pago_preventa"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => {
              return (
                <Input
                  title="Ingresa el pago mensual de preventa"
                  placeholder="Valor en pesos mexicanos"
                  maxLength={40}
                  inputMode="numeric"
                  inputType="money"
                  value={value}
                  onChange={onChange}
                  isValid={!errors.pago_preventa?.message && value.length > 0}
                />
              );
            }}
          />
        )}

        <Controller
          name="plazo"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => {
            return (
              <Dropdown
                title="Plazo"
                placeholder="20 años"
                value={value}
                onChange={onChange}
                options={TERM}
              />
            );
          }}
        />
      </QuoteContent>

      <Tooltip
        type="info"
        title="Tip Presta Prenda"
        body="Si tu cliente elige plazos más largos, su pago mensual será menor. Además puede abonar a capital o liquidar cuando quiera."
      />

      <QuoteButton>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid}
        >
          Cotizar préstamo
        </Button>
      </QuoteButton>
    </QuoteContainer>
  );
};

export default Quote;
